import clsx from "clsx";
import Modal from "../Modal/Modal";
import RadioGroup from "../RadioGroup/RadioGroup";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import CheckBox from "../CheckBox/CheckBox";
import { ICON_SIZES } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import { MdClose } from "react-icons/md";
import useModalsStore from "src/store/modalsStore";
import useCollapsibles, {
  ETablePreferencesMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import useMobile from "src/hooks/useMobile";
import useTablePreferences, {
  EPaginationStyle,
} from "src/hooks/useTablePreferences";
import { EButtonKind } from "../Button/ButtonTypes";

const TableSettingsModal: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const {
    allowMultipleSelection,
    allowPinnedColumns,
    hasBottomPagination,
    hasDoublePaginationEnabled,
    showColumnFilters,
    hasTopPagination,
    selectOnRowClick,
    updateTablePreferences,
    paginationStyle,
  } = useTablePreferences();
  const {
    collapsiblePreferences,
    updateCollapsible,
    isTablePreferencesMobileCollapsibleOpen,
    toggleTablePreferencesMobileCollapsible,
  } = useCollapsibles();

  const { setShowTableSettingsModal } = useModalsStore();

  return (
    <Modal
      onEscape={() => setShowTableSettingsModal(false)}
      className={clsx(["!w-full", "md:!min-w-[550px]", "md:!max-w-[550px]"])}
    >
      <Modal.Header
        onClose={() => setShowTableSettingsModal(false)}
        title="Personalizar tablas"
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-4", "w-full", "pb-2"])}>
          <div className={clsx(["text-sm", "md:text-base"])}>
            Las opciones se aplican de forma general a todas las tablas de la
            aplicación y se guardarán sólo en este dispositivo.
          </div>
          <CollapsibleBlock
            title="Estilo de paginación"
            open={
              isMobile
                ? isTablePreferencesMobileCollapsibleOpen(
                    ETablePreferencesMobileCollapsibles.PAGINATION
                  )
                : collapsiblePreferences.tablePreferencesPaginationOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleTablePreferencesMobileCollapsible(
                  ETablePreferencesMobileCollapsibles.PAGINATION
                );
              } else {
                updateCollapsible({ tablePreferencesPaginationOpen: isOpen });
              }
            }}
          >
            <RadioGroup
              layout="vertical"
              onChange={(value: any) =>
                updateTablePreferences({ paginationStyle: value as any })
              }
              selected={paginationStyle}
              options={[
                {
                  label: "Arriba",
                  description:
                    "Mostrar la paginación en la parte superior de todas las tablas",
                  value: EPaginationStyle.TOP,
                },
                {
                  label: "Abajo",
                  description:
                    "Mostrar la paginación en la parte inferior de todas las tablas",
                  value: EPaginationStyle.BOTTOM,
                },
                {
                  label: "Doble",
                  description:
                    "Mostrar paginación doble (parte superior y parte inferior) en todas las tablas",
                  value: EPaginationStyle.DOUBLE,
                },
              ]}
            />
          </CollapsibleBlock>
          <CollapsibleBlock
            title="Columnas"
            open={
              isMobile
                ? isTablePreferencesMobileCollapsibleOpen(
                    ETablePreferencesMobileCollapsibles.COLUMNS
                  )
                : collapsiblePreferences.tablePreferencesColumnsOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleTablePreferencesMobileCollapsible(
                  ETablePreferencesMobileCollapsibles.COLUMNS
                );
              } else {
                updateCollapsible({ tablePreferencesColumnsOpen: isOpen });
              }
            }}
          >
            <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
              <CheckBox
                checked={allowPinnedColumns}
                onChange={(value) =>
                  updateTablePreferences({ allowPinnedColumns: value })
                }
                label="Habilitar fijado de columnas"
                description="Mostrar en el título el ícono que permite fijar las columnas a la izquierda de las tablas (no todas las columnas soportan esta característica)"
              />
              <CheckBox
                checked={showColumnFilters}
                onChange={(value) =>
                  updateTablePreferences({ showColumnFilters: value })
                }
                label="Habilitar filtros en columnas"
                description="Mostrar el ícono de filtrado en todas las columnas que soporten la característica"
              />
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            title="Comportamiento al hacer click o tap"
            open={
              isMobile
                ? isTablePreferencesMobileCollapsibleOpen(
                    ETablePreferencesMobileCollapsibles.SELECTION
                  )
                : collapsiblePreferences.tablePreferencesSelectionOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleTablePreferencesMobileCollapsible(
                  ETablePreferencesMobileCollapsibles.SELECTION
                );
              } else {
                updateCollapsible({ tablePreferencesSelectionOpen: isOpen });
              }
            }}
          >
            <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
              <CheckBox
                checked={selectOnRowClick}
                onChange={(value) =>
                  updateTablePreferences({ selectionOnClickAnyCell: value })
                }
                label="Selección en cualquier celda"
                description="Permitir seleccionar las filas al hacer click en cualquiera de sus celdas (por defecto sólo se pueden seleccionar por medio del check de la primera columna)"
              />
              <CheckBox
                checked={allowMultipleSelection}
                onChange={(value) =>
                  updateTablePreferences({ disableMultipleSelection: !value })
                }
                label="Permitir selección múltiple"
                description="Permitir seleccionar múltiples filas al hacer click en cualquiera de sus celdas (debes tener la opción anterior activada)"
              />
            </div>
          </CollapsibleBlock>
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("shared.close"),
            onClick: () => setShowTableSettingsModal(false),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.MD} />,
          },
        ]}
      />
    </Modal>
  );
};

export default TableSettingsModal;
